import { getQuarterPricingKey, getCurrentQuarter } from '../constants/quarterConfig.jsx';
import { getServicePartNumbers } from './partNumberUtils.jsx';
import { quarterToDiscountKey } from '../pricing/seedBuilder';
import { log } from './devLog';

// This file contains utility functions for accessing product configuration data from the product database (productDb-AIC.json and productDb-DELL.json)


// Get product price based on and quarter and part number
export const getProductPrice = (productDb , quarter, partNumber) => {
  if (!productDb?.products[partNumber]) return 0;
  return productDb.products[partNumber][getQuarterPricingKey(quarter)] || 0;
};

// Get product nominal and weight power based on part number
export const getProductWeightPower = (productDb, partNumber) => {
  if (!productDb || !productDb.products || !productDb.products[partNumber]) {
    return { nominalPower: 0, weight: 0 };
  }
  const product = productDb.products[partNumber];
  return { nominalPower: product.nominalPower, weight: product.weight };
};

// Get product weight based on part number
export const getProductWeight = (productDb, partNumber) => {
  if (!productDb || !productDb.products || !productDb.products[partNumber]) {
    return 0;
  }
  const product = productDb.products[partNumber];
  return product.weight || 0;
};

// Get product nominal power based on part number
export const getProductPower = (productDb, partNumber) => {
  if (!productDb || !productDb.products || !productDb.products[partNumber]) {
    return 0;
  }
  const product = productDb.products[partNumber];
  return product.nominalPower || 0;
};

// Get product worst-case (max) power based on part number
export const getProductMaxPower = (productDb, partNumber) => {
  if (!productDb?.products?.[partNumber]) return 0;
  return productDb.products[partNumber].maxPower || 0;
};

// Helper function to get a product description
export const getProductDescription = (productDb, partNumber) => {
  if (!productDb?.products[partNumber]) return 0;
  return productDb.products[partNumber].description || "";
};

// Helper function to get a product discount
export const getProductDiscount = (productDb, partNumber, quarter) => {
  const product = productDb?.products[partNumber];
  if (!product) return 0;
  // Per-quarter discount when the price book carries one (a published edition
  // resolved the discount for each quarter); the scalar otherwise. productDb
  // itself has only the scalar, so an un-published vendor takes the fallback.
  if (quarter) {
    const perQuarter = product[quarterToDiscountKey(quarter)];
    if (typeof perQuarter === 'number') return perQuarter;
  }
  return product.discount || 0;
};

//************************************************************************************
// Helper function to calculate default quote expiration date
// 14 days from today, adjusted to next Monday if it falls on a weekend
//************************************************************************************
export const calculateDefaultExpirationDate = () => {
  const today = new Date();
  
  // Add 14 days
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 14);
  
  // Get day of week (0 = Sunday, 6 = Saturday)
  const dayOfWeek = futureDate.getDay();
  
  // If Saturday (6), add 2 days to get to Monday
  if (dayOfWeek === 6) {
    futureDate.setDate(futureDate.getDate() + 2);
  }
  // If Sunday (0), add 1 day to get to Monday
  else if (dayOfWeek === 0) {
    futureDate.setDate(futureDate.getDate() + 1);
  }
  
  // Return in YYYY-MM-DD format (required for input type="date")
  return futureDate.toISOString().split('T')[0];
};

// Helper function to format date as "Month DD, YYYY"
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const monthName = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  
  return `${monthName} ${day}, ${year}`;
};

// MORK TODO : Investigate if needed
// `vendor` is optional and defaults to null (= AIC part numbers) because the
// original caller runs at config-init time, before a vendor exists. Callers
// that DO have one — src/utils/baselinePricing.js — pass it so a DELL quote
// resolves DELL's VDURACare SKUs rather than AIC's.
// `subscriptionMonths` is optional and defaults to undefined (harmless for AIC/DELL,
// whose SKUs don't vary by duration) — vendors with registry-declared per-duration
// SKUs (SMC's 1/3/5-year terms) need it passed through so getServicePartNumbers
// resolves the term-appropriate SKU instead of `undefined` snapping to the minimum.
export const getInitialSoftwarePricing = (
  productDb, quarter = getCurrentQuarter(), serviceOption = "standard", vendor = null, subscriptionMonths,
) => {
  log("getInitialSoftwarePricing called!!!!!!!!!!!!!!!!");
  log('call stack');

  if (!productDb?.products) {
    console.warn('ProductDb not available, using fallback pricing');
    return { ssdSoftware: 225.00, hddSoftware: 16.00 };
  }

  // Use the imported function
  const vduraCarePartNumbers = getServicePartNumbers(serviceOption, vendor, subscriptionMonths); // null vendor = AIC default; vendor not available at init time

  const quarterKey = getQuarterPricingKey(quarter);

  // Get discount price (what we actually charge) from productDb
  const ssdPrice = productDb.products[vduraCarePartNumbers.ssd]?.[quarterKey] || 225.00;
  const hddPrice = productDb.products[vduraCarePartNumbers.hdd]?.[quarterKey] || 16.00;

  log(`Initial Vdura Care pricing for ${quarter} (${serviceOption}): SSD=${ssdPrice}, HDD=${hddPrice}`);

  return {
    ssdSoftware: ssdPrice,
    hddSoftware: hddPrice
  };
};

// Encoding schemes offered per node count. The table lives in the domain rules
// (CONF-207 removed the copy that used to sit here) so the UI can never offer a
// scheme that R1 would immediately reset.
export { availableEncodingSchemes as getAvailableEncodingSchemes } from '../domain/rules/storageRules';

/**
 * Parse encoding scheme helper (re-used from performanceHelpers)
 * 
 * MORK TODO - Make sure this is in a single location and not reused.
 */
export const parseEncodingScheme = (encodingScheme) => {
   
  if (!encodingScheme || typeof encodingScheme !== 'string') {
    console.warn('Invalid encoding scheme provided, defaulting to 4+2');
    encodingScheme = '4+2'; // Default value
  }

  try {
    const [dataBits, parityBits] = encodingScheme.split('+').map(Number);
    
    // Validate the parsed values
    if (isNaN(dataBits) || isNaN(parityBits)) {
      console.warn('Invalid encoding scheme format, defaulting to 4+2');
      return { dataBits: 4, parityBits: 2 };
    }
    //log('dataBits', dataBits);
    //log('parityBits', parityBits);

    return { dataBits, parityBits };
  } catch (error) {
    console.warn('Error parsing encoding scheme, defaulting to 4+2');
    return { dataBits: 4, parityBits: 2};
  }
  
};



/**
 * Conversion constants for Decimal ↔ Binary units
 * 1 TB = 1000^4 bytes = 1,000,000,000,000 bytes
 * 1 TiB = 1024^4 bytes = 1,099,511,627,776 bytes
 * Ratio: 1 TB / 1 TiB = 0.9095 (approximately)
 */
const TB_TO_TIB_RATIO = 0.9095;
const GB_TO_GIB_RATIO = 0.9313;

/**
 * Convert capacity from TB to appropriate unit based on preference
 * @param {number} valueInTB - Capacity value in TB (decimal)
 * @param {boolean} isPBUnit - true for Decimal (TB/PB), false for Binary (TiB/PiB)
 * @returns {number} Converted capacity value
 */
export const convertCapacity = (valueInTB, isPBUnit) => {
  return isPBUnit ? valueInTB : valueInTB * TB_TO_TIB_RATIO;
};

/**
 * Convert throughput from GB/s to appropriate unit based on preference
 * @param {number} valueInGBps - Throughput in GB/s (decimal)
 * @param {boolean} isPBUnit - true for Decimal (GB/s), false for Binary (GiB/s)
 * @returns {number} Converted throughput value
 */
export const convertThroughput = (valueInGBps, isPBUnit) => {
  return isPBUnit ? valueInGBps : valueInGBps * GB_TO_GIB_RATIO;
};

/**
 * Get capacity unit label with optional suffix
 * @param {boolean} isPBUnit - true for Decimal, false for Binary
 * @param {string} suffix - Optional suffix like 'r' (raw), 'e' (effective)
 * @returns {string} Unit label (e.g., 'TBr', 'TiBe')
 */
export const getCapacityUnit = (isPBUnit, suffix = '') => {
  return isPBUnit ? `TB${suffix}` : `TiB${suffix}`;
};

/**
 * Format a per-drive catalog capacity for display (e.g. 7.68 -> "7.68", 20 -> "20").
 * Drive sizes are catalog values, not computed totals - never round them, or a
 * 7.68 TB NVMe reads as "7.7" (toFixed(1)) or "8" (0 fraction digits).
 * @param {number|string} capacityTb - Per-drive capacity in TB
 * @returns {string} Capacity with up to 2 decimals, no trailing zeros
 */
export const formatDriveCapacity = (capacityTb) => {
  const value = Number(capacityTb);
  if (capacityTb === null || capacityTb === '' || isNaN(value)) return '0';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Get throughput unit label
 * @param {boolean} isPBUnit - true for Decimal, false for Binary
 * @returns {string} Unit label ('GB/s' or 'GiB/s')
 */
export const getThroughputUnit = (isPBUnit) => {
  return isPBUnit ? 'GB/s' : 'GiB/s';
};